<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { deletePostAction } from '@/api/functions/settings/blogs-action'
import type { PostItem } from '@/api/typings/settings.typing'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps<{
  blogs: PostItem[]
}>()

const emit = defineEmits<{
  (e: 'refreshBlogs'): void
  (e: 'selectionChange', selectedIds: string[]): void
}>()

const router = useRouter()
const toast = useToast()

const selectedBlogs = ref<Set<string>>(new Set())
const allSelected = ref(false)
const showDeleteModal = ref(false)
const selectedBlogId = ref<string | null>(null)

const goToBlogDetails = (slug: string) => router.push({ path: `/settings/blog/${slug}` })
const goToBlogEdit = (slug: string) => router.push({ path: `/settings/blog/${slug}/edit` })

const deleteMutation = useMutationApi({
  mutationFn: (ids: string[]) => deletePostAction(ids),
  onSuccess: () => {
    toast.success('بلاگ با موفقیت حذف شد')
    showDeleteModal.value = false
    selectedBlogs.value.clear()
    emit('refreshBlogs')
  },
  onError: (error: unknown) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteBlog = () => {
  if (selectedBlogs.value.size === 0) return
  deleteMutation.mutate(Array.from(selectedBlogs.value))
}

const toggleBlogSelection = (blogId: string) => {
  if (selectedBlogs.value.has(blogId)) {
    selectedBlogs.value.delete(blogId)
  } else {
    selectedBlogs.value.add(blogId)
  }
  emit('selectionChange', Array.from(selectedBlogs.value))
}

const toggleSelectAll = () => {
  if (allSelected.value) {
    selectedBlogs.value.clear()
  } else {
    props.blogs.forEach((blog) => selectedBlogs.value.add(blog.id))
  }
  allSelected.value = !allSelected.value
  emit('selectionChange', Array.from(selectedBlogs.value))
}

const confirmDelete = (id: string) => {
  selectedBlogId.value = id
  showDeleteModal.value = true
}

watch(selectedBlogs, (newSet) => {
  allSelected.value = newSet.size === props.blogs.length && props.blogs.length > 0
})
</script>

<template>
    <div>
        <ConfirmModal v-model="showDeleteModal" title="حذف کردن مطالب وبلاگ"
            message="آیا از حذف کردن این مطالب اطمینان دارید؟" confirm-text="بله حذف شود" cancel-text="بازگشت"
            @confirm="selectedBlogId ? deleteMutation.mutate([selectedBlogId]) : handleDeleteBlog()"
            @cancel="showDeleteModal = false" />

        <!-- Header -->
        <div class="p-4 flex items-center gap-8 text-[#1b325a] text-[10px] font-black">
            <div class="w-5 h-5 rounded-md border-[2.5px] flex items-center justify-center cursor-pointer"
                :class="allSelected ? 'bg-[#2d5396] border-[#2d5396]' : 'border-[#858585]'" @click="toggleSelectAll">
                <svg v-if="allSelected" class="w-6 h-6 text-white" fill="none" stroke="currentColor" stroke-width="3"
                    viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                </svg>
            </div>
            <span>تصویر نمایه</span>
            <span>عنوان مطلب</span>
        </div>

        <!-- Body -->
        <div class="p-4 bg-white rounded-xl grid gap-6 mt-4">
            <div v-for="blog in props.blogs" :key="blog.id" class="group flex flex-col gap-3">

                <div class="flex items-center gap-6 cursor-pointer" @click="toggleBlogSelection(blog.id)">
                    <div class="w-5 h-5 rounded-md border-[2.5px]  flex items-center justify-center duration-200"
                        :class="selectedBlogs.has(blog.id) ? 'bg-[#2d5396] border-[#2d5396]' : 'border-[#858585]'">
                        <svg v-if="selectedBlogs.has(blog.id)" class="w-6 h-6 text-white" fill="none"
                            stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                            <path d="M5 13l4 4L19 7" />
                        </svg>
                    </div>

                    <img :src="blog.thumbnail" alt="blog-img"
                        class="w-[66px] lg:w-[147px] h-[66px] lg:h-[147px] rounded-full border border-[#000000de] object-cover" />
                    <p class="text-sm">{{ blog.title }}</p>
                </div>

                <!-- Actions -->
                <div class="mt-3 w-full lg:w-fit flex flex-wrap items-center gap-2 lg:ms-28">
                    <Button @click="goToBlogEdit(blog.slug)" :isAdmin="true"
                        class="w-fit rounded-lg px-3.5 h-8 py-0 text-[13px] font-normal">
                        ویرایش
                    </Button>

                    <Button @click="goToBlogDetails(blog.slug)" :isAdmin="true" variant="outline"
                        class="w-fit border-2 rounded-lg px-3.5 h-8 py-0 text-[13px]">
                        مشاهده
                    </Button>

                    <Button @click="confirmDelete(blog.id)" :isAdmin="true" variant="outline" class="w-fit border-2 border-[#0000001f] rounded-lg px-3.5 h-8 py-0 text-[13px]
                   lg:opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        حذف
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>
